import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Chip,
  Button,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../../../firebase-config";
import { doc, getDoc, updateDoc } from "firebase/firestore";

const AdminQuoteView = () => {
  const { id: quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) return;

    const fetchQuote = async () => {
      try {
        const quoteRef = doc(db, "quotes", quoteId);
        const quoteSnap = await getDoc(quoteRef);
        if (quoteSnap.exists()) {
          setQuote({ id: quoteSnap.id, ...quoteSnap.data() });
        } else {
          setError("Quote not found.");
        }
      } catch (err) {
        console.error("Error fetching quote:", err);
        setError("Failed to load quote.");
      } finally {
        setLoading(false);
      }
    };

    const checkAdminStatus = async () => {
      try {
        const adminRef = doc(db, "admins", currentUser.uid);
        const adminSnap = await getDoc(adminRef);
        setIsAdmin(adminSnap.exists());
      } catch (err) {
        console.error("Error checking admin status:", err);
        setError("Failed to verify admin access.");
      }
    };

    fetchQuote();
    checkAdminStatus();
  }, [currentUser, quoteId]);

  const handleUpdateStatus = async (newStatus) => {
    if (!isAdmin) return;
    try {
      const quoteRef = doc(db, "quotes", quoteId);
      await updateDoc(quoteRef, { status: newStatus });
      setQuote((prevQuote) => ({ ...prevQuote, status: newStatus }));
    } catch (err) {
      console.error("Error updating quote status:", err);
      setError("Failed to update quote.");
    }
  };

  const calculateTotalPrice = () => {
    return quote.items
      .reduce(
        (total, item) => total + (item.price + (item.optionPrice || 0)) * item.quantity,
        0
      )
      .toFixed(2);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Quote Details
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6">
            Quote ID: {quoteId}{" "}
            <Chip
              label={quote.status.toUpperCase()}
              color={
                quote.status === "pending"
                  ? "warning"
                  : quote.status === "approved"
                  ? "success"
                  : "error"
              }
              sx={{ ml: 1 }}
            />
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Requested by: {quote.userEmail}
          </Typography>
          {quote.companyId && (
            <Typography variant="body2" color="textSecondary">
              Company ID: {quote.companyId}
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary">
            Requested on: {new Date(quote.createdAt.toDate()).toLocaleString()}
          </Typography>

          
          <Typography variant="body2" color="textSecondary">
            Note: {quote.note}
          </Typography>

        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom>
        Items
      </Typography>

      <List>
        {quote.items.map((item, index) => (
          <React.Fragment key={index}>
            <ListItem>
              <ListItemText
                primary={`${item.name} ${item.selectedOption ? `(${item.selectedOption})` : ""}`}
                secondary={`Quantity: ${item.quantity} | Base Price: $${item.price.toFixed(2)}${
                  item.optionPrice ? ` | Option Price: $${item.optionPrice.toFixed(2)}` : ""
                } | Total: $${((item.price + (item.optionPrice || 0)) * item.quantity).toFixed(2)}`}
              />
            </ListItem>
            {index < quote.items.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Total Price: ${calculateTotalPrice()}</Typography>
      </Box>

      {isAdmin && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="success"
            sx={{ mr: 2 }}
            onClick={() => handleUpdateStatus("approved")}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleUpdateStatus("rejected")}
          >
            Reject
          </Button>
        </Box>
      )}

      <Button variant="outlined" onClick={() => navigate("/admin/quotes")} sx={{ mt: 3 }}>
        Back to Quotes
      </Button>
    </Container>
  );
};

export default AdminQuoteView;
